/**
 * PhoneSaleDetail — R7a (2026-05-18).
 *
 * Read-only detail view for a single phone-sale row. Reuses the
 * get_phone_sales_report RPC (filtered to the activity_id via a
 * client-side find — the row count is small and the RPC enforces all
 * access gating). Includes a cancel button for creator + admin/manager.
 *
 * Edit functionality is deferred; today the only mutation from the UI
 * is cancel_phone_sale.
 */

import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPhoneSaleById,
  cancelPhoneSale,
} from "../../services/salesActivities.service";
import {
  resolvePositionNamesBulk,
  indexPositionsById,
} from "../../services/reportsSuite.service";

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "—";
  }
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const PhoneSaleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Fetch the single row by id (its own cache key) so navigating straight to a
  // just-created sale reads THAT row, not a stale shared report window — which
  // flashed "not found" and drove duplicate submissions (Batch 1b).
  const {
    data: row,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["phone-sale-by-id", id],
    queryFn: () => getPhoneSaleById(id!),
    enabled: !!id,
  });

  const { data: positionRows } = useQuery({
    queryKey: ["phone-sale-detail-position", row?.rep_position_id],
    queryFn: () =>
      row?.rep_position_id
        ? resolvePositionNamesBulk([row.rep_position_id])
        : Promise.resolve([]),
    enabled: Boolean(row?.rep_position_id),
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "—";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const handleCancel = async () => {
    if (!row) {
      return;
    }
    if (
      !confirm(
        `Cancel this phone sale? It will be soft-cancelled (excluded from reports) but kept in the audit trail.`,
      )
    ) {
      return;
    }
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelPhoneSale(row.activity_id);
      await queryClient.invalidateQueries({
        queryKey: ["phone-sale-by-id", id],
      });
      await queryClient.invalidateQueries({ queryKey: ["phone-sales-list"] });
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : "Could not cancel.");
    } finally {
      setCancelling(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        {(error as Error).message}
      </div>
    );
  }
  if (!row) {
    return (
      <div className="space-y-4">
        <Link
          to="/sales-activities/phone-sales"
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back to phone sales
        </Link>
        <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
          Phone sale not found, or you don't have access to it.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <Link
        to="/sales-activities/phone-sales"
        className="text-sm text-blue-600 hover:underline"
      >
        ← Back to phone sales
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Phone Sale</h1>
          <p className="mt-1 text-sm text-gray-500 font-mono">
            {row.activity_id}
          </p>
        </div>
        {row.status !== "cancelled" && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={cancelling}
            className="rounded border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {cancelling ? "Cancelling…" : "Cancel this phone sale"}
          </button>
        )}
      </div>

      {cancelError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {cancelError}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Status
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{row.status}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Created
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatDate(row.created_at)}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Credited Rep
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {repDisplay(row.rep_position_id)}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Contact
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {row.contact_id ? (
                <Link
                  to={`/contacts/${row.contact_id}`}
                  className="text-blue-600 hover:underline"
                >
                  {[row.contact_first_name, row.contact_last_name]
                    .filter(Boolean)
                    .join(" ") || row.contact_id}
                </Link>
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Email
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {row.contact_email || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Phone
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {row.contact_phone || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Member #
            </dt>
            <dd className="mt-1 text-sm text-gray-900">
              {row.member_number || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-gray-500">
              Organization
            </dt>
            <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">
              {row.organization_id || "—"}
            </dd>
          </div>
          {row.notes && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase text-gray-500">
                Notes
              </dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                {row.notes}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
};

export default PhoneSaleDetail;
